import type { ComponentType } from 'react';
import { createBrowserRouter, type NonIndexRouteObject, type RouteObject } from 'react-router';
import { LoginPage } from '../auth/LoginPage.tsx';
import { RequireSession } from '../auth/RequireSession.tsx';
import { AdminLayout } from '../layout/AdminLayout.tsx';
import { NotFoundPage } from '../pages/NotFoundPage.tsx';
import { RouteError } from '../pages/RouteError.tsx';
import { RouteLoading } from '../pages/RouteLoading.tsx';
import { SectionPage } from '../pages/SectionPage.tsx';
import { ALL_SECTIONS, type SectionId } from './sections.ts';

type PageLoader = () => Promise<ComponentType>;
const page = (load: PageLoader): NonIndexRouteObject => ({ lazy: async () => ({ Component: await load() }) });

/*
 * The sections connected to the API, each in its own chunk. The rest keep their empty page until
 * their part of the API exists.
 */
const PAGES: Partial<Record<SectionId, PageLoader>> = {
  codes: async () => (await import('../sections/codes/AccessCodesPage.tsx')).AccessCodesPage,
  chats: async () => (await import('../sections/chat/ChatsPage.tsx')).ChatsPage,
  dispatchCenter: async () => (await import('../sections/growth/CentralPage.tsx')).CentralPage,
  proposals: async () => (await import('../sections/growth/BusinessToolsPage.tsx')).BusinessToolsPage,
  leads: async () => (await import('../sections/growth/LeadsPage.tsx')).LeadsPage,
  corporate: async () => (await import('../sections/growth/CompaniesPage.tsx')).CompaniesPage,
  campaigns: async () => (await import('../sections/growth/CampaignsPage.tsx')).CampaignsPage,
  replyTemplates: async () => (await import('../sections/growth/ClaimTemplatesPage.tsx')).ClaimTemplatesPage,
  testing: async () => (await import('../sections/testing/TestAccountsPage.tsx')).TestAccountsPage,
  audit: async () => (await import('../sections/audit/AuditPage.tsx')).AuditPage,
  production: async () => (await import('../sections/production/ProductionPage.tsx')).ProductionPage,
  home: async () => (await import('../sections/home/HomePage.tsx')).HomePage,
  alerts: async () => (await import('../sections/alerts/AlertsPage.tsx')).AlertsPage,
  orders: async () => (await import('../sections/orders/OrdersPage.tsx')).OrdersPage,
  trips: async () => (await import('../sections/trips/TripsPage.tsx')).TripsPage,
  payments: async () => (await import('../sections/payments/PaymentsPage.tsx')).PaymentsPage,
  claims: async () => (await import('../sections/claims/ClaimsPage.tsx')).ClaimsPage,
  cash: async () => (await import('../sections/cash/CashPage.tsx')).CashPage,
  payouts: async () => (await import('../sections/payouts/PayoutsPage.tsx')).PayoutsPage,
  fares: async () => (await import('../sections/fares/FaresPage.tsx')).FaresPage,
  revenue: async () => (await import('../sections/revenue/RevenuePage.tsx')).RevenuePage,
  rates: async () => (await import('../sections/rates/RatesPage.tsx')).RatesPage,
  purchases: async () => (await import('../sections/purchases/PurchasesPage.tsx')).PurchasesPage,
  expenses: async () => (await import('../sections/expenses/ExpensesPage.tsx')).ExpensesPage,
  stats: async () => (await import('../sections/stats/MetricsPage.tsx')).MetricsPage,
  warehouse: async () => (await import('../sections/warehouse/WarehousePage.tsx')).WarehousePage,
  businesses: async () => (await import('../sections/businesses/BusinessesPage.tsx')).BusinessesPage,
  localMenu: async () => (await import('../sections/local-menu/LocalMenuOverviewPage.tsx')).LocalMenuOverviewPage,
  menuChanges: async () => (await import('../sections/local-menu/ChangesPage.tsx')).ChangesPage,
  businessChats: async () => (await import('../sections/chat/ChatsPage.tsx')).BusinessChatsRedirect,
  catalog: async () => (await import('../sections/catalog/CatalogPage.tsx')).CatalogPage,
  icons: async () => (await import('../sections/icons/IconLibraryPage.tsx')).IconLibraryPage,
  inventory: async () => (await import('../sections/inventory/InventoryPage.tsx')).InventoryPage,
  zones: async () => (await import('../sections/zones/ZonesPage.tsx')).ZonesPage,
  stories: async () => (await import('../sections/stories/StoriesPage.tsx')).StoriesPage,
  webHome: async () => (await import('../sections/stories/WebHomePage.tsx')).WebHomePage,
  reels: async () => (await import('../sections/reels/ReelsPage.tsx')).ReelsPage,
  coupons: async () => (await import('../sections/coupons/CouponsPage.tsx')).CouponsPage,
  drivers: async () => (await import('../sections/drivers/DriversPage.tsx')).DriversPage,
  customers: async () => (await import('../sections/customers/CustomersPage.tsx')).CustomersPage,
  team: async () => (await import('../sections/team/TeamPage.tsx')).TeamPage,
  help: async () => (await import('../sections/help/HelpPage.tsx')).HelpPage,
  ratings: async () => (await import('../sections/ratings/RatingsPage.tsx')).RatingsPage,
  comments: async () => (await import('../sections/comments/CommentsPage.tsx')).CommentsPage,
  marketSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  tripSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  tripsAds: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  commerceSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  driverSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  moneySettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  customerSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  mapSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  emailSettings: async () => (await import('../sections/settings/EmailSettingsPage.tsx')).EmailSettingsPage,
  noticeSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  testSettings: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).AreaSettingsPage,
  services: async () => (await import('../sections/settings/AreaSettingsPage.tsx')).ConnectedServicesPage,
  config: async () => (await import('../sections/settings/SettingsPage.tsx')).SettingsPage,
  manual: async () => (await import('../sections/manual/ManualPage.tsx')).ManualPage,
  features: async () => (await import('../sections/features/FeaturesPage.tsx')).FeaturesPage,
};

const sectionRoutes: RouteObject[] = ALL_SECTIONS.map(section => {
  const load = PAGES[section.id as SectionId];
  if (section.path === '') return load ? { index: true, lazy: async () => ({ Component: await load() }) } : { index: true, element: <SectionPage section={section} /> };
  if (load) return { path: section.path, ...page(load) };
  return { path: section.path, element: <SectionPage section={section} /> };
});

const detailRoutes: RouteObject[] = [
  { path: 'businesses/:businessId', ...page(async () => (await import('../sections/businesses/BusinessDetailPage.tsx')).BusinessDetailPage) },
  { path: 'stories/new', ...page(async () => (await import('../sections/stories/StoryEditorPage.tsx')).StoryEditorPage) },
  { path: 'stories/:storyId', ...page(async () => (await import('../sections/stories/StoryEditorPage.tsx')).StoryEditorPage) },
  { path: 'manual/:chapterId', ...page(async () => (await import('../sections/manual/ManualPage.tsx')).ManualPage) },
  { path: 'drivers/:driverId', ...page(async () => (await import('../sections/drivers/DriverDetailPage.tsx')).DriverDetailPage) },
];

// The component catalog exists only while developing; the build leaves it out.
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [{ path: 'components', lazy: async () => ({ Component: (await import('../showcase/ComponentsPage.tsx')).ComponentsPage }) }]
  : [];

export const router = createBrowserRouter([
  { path: '/join', element: <LoginPage />, errorElement: <RouteError /> },
  {
    path: '/',
    element: <RequireSession />,
    errorElement: <RouteError />,
    // The sections load in their own chunk: this shows while the first one arrives.
    hydrateFallbackElement: <RouteLoading />,
    children: [
      {
        element: <AdminLayout />,
        children: [...sectionRoutes, ...detailRoutes, ...devRoutes, { path: '*', element: <NotFoundPage /> }],
      },
    ],
  },
]);
